import React from 'react';
import { twMerge } from 'tailwind-merge';

export interface FeatureRadioProps {
  id: string;
  title: string;
  description: string;
  checkedContent?: React.ReactNode;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

export const FeatureRadio: React.FC<FeatureRadioProps> = ({
  id,
  title,
  description,
  checked = false,
  checkedContent = null,
  onChange,
  disabled = false,
  className,
}) => {
  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      id={id}
      role="radio"
      aria-checked={checked}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={twMerge(
        'flex w-full flex-col rounded-lg py-4 pr-2 pl-4 transition-colors duration-200',
        'border-chariot-stroke border-2',
        'focus-visible:ring-chariot-brand-primary focus-visible:rounded-lg focus-visible:ring-2 focus-visible:outline-none',
        checked &&
          'border-chariot-brand-primary bg-chariot-background-secondary',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      {/* Clickable header area: radio circle + title/description */}
      <div
        className="flex cursor-pointer items-center gap-4"
        onClick={e => {
          e.stopPropagation();
          handleClick();
        }}
      >
        {/* Radio Circle */}
        <div
          className={twMerge(
            'relative -mt-4 size-5 shrink-0 rounded-full border-2',
            'flex items-center justify-center',
            checked ? 'border-chariot-brand-primary' : 'border-chariot-stroke'
          )}
        >
          <div
            className={twMerge(
              'bg-chariot-brand-primary size-3 rounded-full transition-all duration-200',
              checked ? 'scale-100' : 'scale-0'
            )}
          />
        </div>

        {/* Text content */}
        <div className="text-left">
          <p className="text-chariot-text-primary text-sm font-semibold">
            {title}
          </p>
          <p className="text-chariot-text-secondary mt-0.5 text-xs">
            {description}
          </p>
        </div>
      </div>

      {/* checkedContent is outside the clickable area to allow interactive elements */}
      {checked && checkedContent && (
        <div className="mt-4 ml-9">{checkedContent}</div>
      )}
    </div>
  );
};

export default FeatureRadio;
